import React, { useEffect, useMemo, useState } from 'react';
import { Printer, RefreshCw, Plug, Wrench, ChevronDown, Copy, Unplug, Loader2, Check } from 'lucide-react';
import { Drawer } from '../../ui/Drawer';
import { Button } from '../../ui/Button';
import { toast } from '../../lib/buses';
import { usePrinter, connect, disconnect, refresh, updateLayout, printTickets } from '../../lib/ticketPrinter/printerStore';
import { TEMPLATES, DEFAULT_TEMPLATE } from '../../lib/ticketPrinter/templates';
import { decodeStatus, rawHex, STATUS_QUERY_NAMES } from '../../lib/masung-printer/status';
import { deviceKey, missingEssentials, CHECK_NAMES } from '../../lib/masung-printer/profile';
import { saveProfile } from '../../lib/ticketPrinter/printerStore';
import { sampleFace } from '../../lib/ticketPrinter/ticketFace';
import { TicketPreview } from './TicketPreview';
import PrinterSetupWizard from './setup/PrinterSetupWizard';

/**
 * THE TICKET PRINTER, FROM THE DESK.
 *
 * Top to bottom in the order a person needs it: what is going on and what to do about it; which
 * printer this is and whether it was ever set up for this stock; how the ticket is laid out (seen
 * before a ticket is spent); and — folded away, because it is for whoever is helping on the phone —
 * the raw evidence.
 */

export const TONE = {
  red: { dot: 'bg-[#C0392B]', box: 'border-[#F1D2D2] bg-[#FDF3F2]', text: 'text-[#9B1F1F]' },
  amber: { dot: 'bg-[#D08A11]', box: 'border-[#F0DDB0] bg-[#FDF8EC]', text: 'text-[#8A5A00]' },
  green: { dot: 'bg-[#2F8B4F]', box: 'border-[#CFE6D6] bg-[#F2FAF4]', text: 'text-[#1F6A3A]' },
  blue: { dot: 'bg-[#2F6FB0]', box: 'border-[#CFDDF0] bg-[#F2F6FC]', text: 'text-[#1F4F80]' },
  grey: { dot: 'bg-[#9CA3AF]', box: 'border-[#E6E4E1] bg-[#FBFAF7]', text: 'text-[#4B5563]' },
};

const ago = (t) => {
  if (!t) return null;
  const s = Math.round((Date.now() - t) / 1000);
  return s < 5 ? 'just now' : s < 60 ? `${s}s ago` : `${Math.round(s / 60)}m ago`;
};

/** The situation, the instruction, the one button. Shared by the panel and the print sheet. */
export const SituationCard = ({ onSetup, compact = false }) => {
  const p = usePrinter();
  const s = p.situation;
  const t = TONE[s.tone];
  const [busy, setBusy] = useState(false);
  const act = async () => {
    setBusy(true);
    try {
      if (s.action === 'connect') { const f = await connect(); if (f) toast(f, 'error'); }
      else if (s.action === 'setup') onSetup?.();
      else await refresh();
    } finally { setBusy(false); }
  };
  const label = { connect: 'Connect printer', setup: 'Set up printer', recheck: 'Check again' }[s.action];
  const confirmGood = () => {
    const prof = p.profile;
    if (!prof) return;
    const at = new Date().toISOString();
    saveProfile({ ...prof, verifiedAt: at, checks: { ...prof.checks, verify: { status: 'pass', at, evidence: 'Operator confirmed a real ticket from this printer: cut on the perforation, layout clear of the stock’s printing, QR scans.' } } });
    toast('Printer set up', 'success', 'Verified on a real ticket');
  };
  return (
    <div className={`rounded-[10px] border ${t.box} px-4 py-3`}>
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${t.dot} ${s.tone === 'blue' ? 'animate-pulse' : ''}`} />
        <div className="min-w-0 flex-1">
          <div className={`text-[14px] font-semibold ${t.text}`}>{s.headline}</div>
          {s.todo && <div className="mt-1 text-[12.5px] text-[#374151] leading-relaxed">{s.todo}</div>}
          {!compact && p.connected && (
            <div className="mt-1.5 text-[10.5px] text-[#9CA3AF] font-mono">checked {ago(p.statusAt) ?? '…'}{s.evidence ? ` · ${s.evidence}` : ''}</div>
          )}
        </div>
        {s.action === 'verify' && (
          <div className="flex flex-col gap-1.5 shrink-0">
            <Button size="sm" variant="primary" icon={Check} onClick={confirmGood}>Came out right</Button>
            <Button size="sm" variant="ghost" onClick={() => onSetup?.()}>Something’s wrong</Button>
          </div>
        )}
        {label && (
          <Button size="sm" variant={s.tone === 'red' || s.action === 'connect' || s.action === 'setup' ? 'primary' : 'secondary'} onClick={act} disabled={busy} icon={busy ? Loader2 : s.action === 'connect' ? Plug : s.action === 'setup' ? Wrench : RefreshCw}>
            {label}
          </Button>
        )}
      </div>
    </div>
  );
};

const mmOf = (dots) => Math.round(((dots ?? 0) / 8) * 10) / 10;
const dotsOf = (mm) => Math.max(0, Math.round(mm * 8));

const Stepper = ({ label, hint, value, onChange, min = 0, max = 40, step = 1, unit = 'mm' }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <div className="min-w-0">
      <div className="text-[12.5px] font-medium text-[#1F2937]">{label}</div>
      {hint && <div className="text-[11px] text-[#6B7280]">{hint}</div>}
    </div>
    <div className="flex items-center gap-1 shrink-0">
      <button type="button" className="w-7 h-7 rounded border border-[#E6E4E1] hover:bg-[#FAF9F6] text-[#4B5563]" onClick={() => onChange(Math.max(min, Math.round((value - step) * 10) / 10))}>−</button>
      <span className="w-16 text-center text-[12.5px] font-mono tabular-nums">{value} {unit}</span>
      <button type="button" className="w-7 h-7 rounded border border-[#E6E4E1] hover:bg-[#FAF9F6] text-[#4B5563]" onClick={() => onChange(Math.min(max, Math.round((value + step) * 10) / 10))}>+</button>
    </div>
  </div>
);

export const PrinterPanel = ({ open, onClose }) => {
  const p = usePrinter();
  const prof = p.profile;
  const [setupOpen, setSetupOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => { if (open) void refresh(); }, [open]);
  // Setup takes the whole screen and the person's attention — the drawer gets out of its way.
  const startSetup = () => { onClose?.(); setSetupOpen(true); };

  const reported = prof?.reported ?? {};
  // A check added since (e.g. Type & columns) un-finishes an old setup — say which, not "verified".
  const missing = prof?.paperType ? missingEssentials(prof) : [];
  // From the checks themselves, dated by the real-ticket check — not a stored flag that can drift.
  const setupDone = !!prof?.paperType && missing.length === 0;
  const verifiedOn = prof?.checks?.verify?.at ?? prof?.verifiedAt;
  const setupLine = !prof?.paperType ? 'Not set up on this computer'
    : setupDone ? `Set up and verified${verifiedOn ? ` ${new Date(verifiedOn).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}`
    : missing.length ? `Setup needs: ${missing.map((k) => CHECK_NAMES[k] ?? k).join(', ')}`
    : 'Setup started, not finished';

  // Width is moved symmetrically: the centre of the print area stays on the centre of the stock.
  const setWidthMm = (mm) => {
    if (!prof?.paperWidthDots) return;
    const w = dotsOf(mm);
    const centre = (prof.paperLeftDots ?? 0) + prof.paperWidthDots / 2;
    updateLayout({ paperWidthDots: w, paperLeftDots: Math.max(0, Math.round(centre - w / 2)) });
  };

  const testPrint = async () => {
    setTesting(true);
    const job = await printTickets([sampleFace(prof?.reported?.['Model'] ? 'TEST PRINT' : 'TEST PRINT')], 'Test ticket');
    setTesting(false);
    if (job.results[0] === 'printed') toast('Test ticket sent — no error reported', 'success', 'Check the layout and that the cut sits on the perforation');
    else toast('Test ticket was not printed', 'error', job.stopReason);
  };

  const report = () => JSON.stringify({ situation: p.situation, status: p.status, profile: prof, wire: p.printer.wire.slice(-200), log: p.printer.log.slice(-80), userAgent: navigator.userAgent }, null, 2);

  return (
    <>
      <Drawer open={open} onClose={onClose} subtitle="Ticket printer" title={p.deviceName || 'Ticket printer'} width="w-[540px]">
        <div className="px-6 py-5 space-y-6">
          <SituationCard onSetup={startSetup} />
          {p.connected && !prof?.paperType && <ImportSetup />}

          {p.connected && (
            <section>
              <div className="text-[10.5px] tracking-[0.08em] font-semibold uppercase text-[#9CA3AF] mb-2">This printer</div>
              <div className="rounded-[10px] border border-[#E6E4E1] divide-y divide-[#EEEDEA] text-[12.5px]">
                <Row k="Model" v={[reported['Model'], reported['Firmware']].filter(Boolean).join(' · ') || p.deviceName} />
                <Row k="Serial" v={prof?.identity?.serialNumber ?? p.printer.identity()?.serialNumber} mono />
                <Row k="Stock" v={prof?.paperType ? { gap: 'Notched / gap — finds each ticket by its slot', mark: 'Black mark — finds each ticket by its mark', continuous: 'Plain roll — cut by length' }[prof.paperType] : null} />
                {prof?.sensorNote && <Row k="Sensor position" v={prof.sensorNote} />}
                <div className="px-3 py-2 flex items-center justify-between gap-3">
                  <span className={setupDone ? 'text-[#1F6A3A]' : 'text-[#8A5A00]'}>{setupLine}</span>
                  <Button size="sm" variant="secondary" icon={Wrench} onClick={startSetup}>{prof?.paperType ? 'Setup' : 'Set up'}</Button>
                </div>
              </div>
            </section>
          )}

          {p.connected && prof?.paperType && (
            <section>
              <div className="text-[10.5px] tracking-[0.08em] font-semibold uppercase text-[#9CA3AF] mb-2">Ticket layout</div>
              <div className="grid grid-cols-[1fr_auto] gap-5">
                <div className="divide-y divide-[#EEEDEA]">
                  <div className="py-2">
                    <span className="block text-[12.5px] font-medium text-[#1F2937] mb-1.5">Template</span>
                    <div className="flex flex-col gap-1.5">
                      {TEMPLATES.map((t) => (
                        <label key={t.id} className="flex items-start gap-2 cursor-pointer">
                          <input type="radio" name="ticket-template" className="mt-0.5 accent-[#1F2937]" checked={(prof.template ?? DEFAULT_TEMPLATE) === t.id} onChange={() => updateLayout({ template: t.id })} />
                          <span className="min-w-0"><span className="block text-[12px] text-[#1F2937]">{t.name}</span><span className="block text-[11px] text-[#6B7280]">{t.note}</span></span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-start justify-between gap-3 py-2 cursor-pointer">
                    <span className="min-w-0">
                      <span className="block text-[12.5px] font-medium text-[#1F2937]">Event name at the top</span>
                      <span className="block text-[11px] text-[#6B7280]">Turn off when the ticket stock already has the event printed on it.</span>
                    </span>
                    <input type="checkbox" className="mt-1 w-4 h-4 accent-[#1F2937]" checked={prof.showHeader ?? true} onChange={(e) => updateLayout({ showHeader: e.target.checked })} />
                  </label>
                  <Stepper label="Side margin" hint="Keeps text off a printed border." value={mmOf(prof.marginDots)} onChange={(v) => updateLayout({ marginDots: dotsOf(v) })} max={20} step={0.5} />
                  <Stepper label="Space at the top" hint="Clears artwork printed across the top." value={mmOf(prof.topDots)} onChange={(v) => updateLayout({ topDots: dotsOf(v) })} max={40} step={1} />
                  {prof.paperWidthDots && (
                    <Stepper label="Print width" hint="Measured in setup; centred on the stock." value={mmOf(prof.paperWidthDots)} onChange={setWidthMm} min={30} max={mmOf(prof.headDots ?? 832)} step={1} />
                  )}
                  <div className="pt-3 flex items-center gap-2">
                    <Button size="sm" variant="secondary" icon={testing ? Loader2 : Printer} disabled={testing || p.situation.blocksPrinting} onClick={testPrint}>Print a test ticket</Button>
                    <span className="text-[11px] text-[#9CA3AF]">{p.printer.needsAlignment ? 'Layout changed — the next print starts with one blank test slip.' : 'Uses one ticket.'}</span>
                  </div>
                </div>
                <TicketPreview profile={prof} />
              </div>
            </section>
          )}

          <section>
            <button type="button" onClick={() => setDiagOpen((v) => !v)} className="text-[10.5px] tracking-[0.08em] font-semibold uppercase text-[#9CA3AF] inline-flex items-center gap-1">
              <ChevronDown className={`w-3 h-3 transition-transform ${diagOpen ? '' : '-rotate-90'}`} />Diagnostics
            </button>
            {diagOpen && <><Diagnostics status={p.status} reported={reported} wire={p.printer.wire} report={report} connected={p.connected} />{p.connected && prof?.paperType && <TypeCheck prof={prof} printer={p.printer} blocked={p.situation.blocksPrinting} />}</>}
          </section>
        </div>
      </Drawer>
      <PrinterSetupWizard open={setupOpen} onOpenChange={setSetupOpen} />
    </>
  );
};

const Row = ({ k, v, mono }) => (
  <div className="px-3 py-2 flex items-start justify-between gap-4">
    <span className="text-[#6B7280] shrink-0">{k}</span>
    <span className={`text-right text-[#1F2937] ${mono ? 'font-mono text-[11.5px]' : ''}`}>{v || <span className="text-[#9CA3AF]">—</span>}</span>
  </div>
);

/**
 * WHEN A TICKET LOOKS WRONG — which of the commands a designed ticket uses this printer draws.
 * A tool, not a requirement: a printer that prints right tickets needs none of it. Each "no" makes
 * tickets fall back (no 3× type, stacked columns, accents dropped, underline rules).
 */
const TYPE_QS = [
  ['tall', 'Is “H3 H4” big — about 4 lines tall, letters whole?'],
  ['columns', 'Does SEAT start at the middle of the ticket?'],
  ['accents', 'Does it read OUÉDRAOGO · ÇA · NUÑEZ (É Ç Ñ, not junk)?'],
  ['rasterRule', 'Under RULES, is the upper line solid?'],
  ['underlineRule', 'Under RULES, is the lower line solid?'],
  ['serif', 'Is “SERIF 2026” in letters with serifs?'],
  ['frame', 'Is FRAME inside a closed double-line box?'],
];
const TypeCheck = ({ prof, printer, blocked }) => {
  const [busy, setBusy] = useState(false);
  const caps = prof.typeCaps ?? {};
  const set = (k, v) => saveProfile({ ...prof, typeCaps: { tall: true, columns: true, accents: true, rasterRule: true, underlineRule: true, serif: true, frame: true, ...prof.typeCaps, [k]: v } });
  const print = async () => {
    setBusy(true);
    try { await printer.printTypeSlip(prof.paperType === 'continuous' ? 'cut' : 'tear', prof.showHeader ?? true); }
    catch (e) { toast('Type check not printed', 'error', e?.message ?? String(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 rounded-[10px] border border-[#E6E4E1] px-3 py-3 space-y-2">
      <div className="text-[12.5px] font-medium text-[#1F2937]">Ticket looks wrong? Check what this printer can draw</div>
      <p className="text-[11.5px] text-[#6B7280]">Prints one numbered slip. For anything it can't draw, tickets switch to something it can.</p>
      <Button size="sm" variant="secondary" icon={busy ? Loader2 : Printer} disabled={busy || blocked} onClick={print}>Print type check</Button>
      <div className="divide-y divide-[#EEEDEA] text-[12px]">
        {TYPE_QS.map(([k, q], i) => (
          <div key={k} className="py-1.5 flex items-center justify-between gap-3">
            <span className="text-[#374151]">{i + 1}. {q}</span>
            <span className="flex gap-1 shrink-0">
              {[['Yes', true], ['No', false]].map(([l, v]) => (
                <button key={l} type="button" onClick={() => set(k, v)}
                  className={`px-2 py-0.5 rounded-[6px] border text-[11.5px] ${prof.typeCaps && caps[k] === v ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'border-[#D9D6D0] text-[#374151]'}`}>{l}</button>
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Diagnostics = ({ status, reported, wire, report, connected }) => {
  const readings = useMemo(() => (status ?? []).map((b, i) => decodeStatus(i + 1, b)), [status]);
  return (
    <div className="mt-3 space-y-3">
      <p className="text-[11.5px] text-[#6B7280]">What the printer itself reported. Useful when someone is helping you over the phone — or press Copy and send it.</p>
      {readings.length > 0 && (
        <div className="rounded-[10px] border border-[#E6E4E1] divide-y divide-[#EEEDEA] text-[12px]">
          {readings.map((r) => (
            <div key={r.query} className="px-3 py-1.5 flex items-start justify-between gap-3">
              <span className="text-[#6B7280] shrink-0 w-32">{STATUS_QUERY_NAMES[r.query]}</span>
              <span className="flex-1 text-[#1F2937]">{r.flags.map((f) => f.label).join(' · ')}</span>
              <span className="font-mono text-[11px] text-[#9CA3AF]">{rawHex(r.raw)}</span>
            </div>
          ))}
        </div>
      )}
      {Object.keys(reported).length > 0 && (
        <div className="rounded-[10px] border border-[#E6E4E1] divide-y divide-[#EEEDEA] text-[12px]">
          {Object.entries(reported).filter(([, v]) => v).map(([k, v]) => <Row key={k} k={k} v={v} mono />)}
        </div>
      )}
      <div className="rounded-[10px] border border-[#E6E4E1] bg-[#FBFAF7] max-h-48 overflow-y-auto px-3 py-2 font-mono text-[10.5px] leading-[1.5]">
        {wire.slice(-60).map((e, i) => (
          <div key={i} className={e.dir === 'rx' ? 'text-[#1F6A3A] font-semibold' : e.dir === 'tx' ? 'text-[#1F4F80]' : 'text-[#9CA3AF] italic'}>
            {new Date(e.t).toLocaleTimeString('en-GB')} {e.dir === 'tx' ? '→' : e.dir === 'rx' ? '←' : '·'} {e.hex}
          </div>
        ))}
        {!wire.length && <div className="text-[#9CA3AF]">Nothing sent yet.</div>}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" icon={Copy} onClick={async () => { await navigator.clipboard.writeText(report()); toast('Printer report copied', 'success', 'Paste it into the support chat'); }}>Copy report</Button>
        {connected && <Button size="sm" variant="ghost" icon={Unplug} onClick={() => disconnect()}>Disconnect</Button>}
      </div>
    </div>
  );
};

/**
 * SET UP ALREADY, ELSEWHERE. The box office and the back office are different sites, and a browser
 * keeps each site's saved setup apart — so a printer set up at the till shows "Not set up" here, on
 * the same computer. Its support report (box office → Setup → Summary → Copy support report) carries
 * the whole setup; pasting it here adopts it.
 *
 * ONLY FOR THE SAME PRINTER. The serial in the report must match the one plugged in: another unit's
 * offsets and paper type are exactly how "626" ended up on a printer it was never measured on.
 */
const ImportSetup = () => {
  const p = usePrinter();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [err, setErr] = useState(null);
  const here = p.printer.identity();
  const doImport = () => {
    setErr(null);
    let src;
    try { const j = JSON.parse(text); src = j.profile ?? j; } catch { setErr("That isn't a support report — copy it again from the box office's printer setup Summary."); return; }
    if (!src?.paperType || !src?.identity) { setErr('That report has no finished setup in it.'); return; }
    const theirs = src.identity.serialNumber || src.identity.productName;
    const mine = here?.serialNumber || here?.productName;
    if (!here || theirs !== mine) { setErr(`That setup is for a different printer (serial ${theirs || 'unknown'}); this one is ${mine || 'unknown'}. Run setup for this printer instead.`); return; }
    const at = new Date().toISOString();
    saveProfile({ ...src, key: deviceKey(here), identity: here, updatedAt: at,
      checks: { ...src.checks, imported: { status: 'pass', at, evidence: `Imported from the box office setup of the same printer (serial ${mine}).` } } });
    toast('Setup imported', 'success', 'Print a test ticket to confirm the layout here');
    setOpen(false);
  };
  return (
    <div className="text-[12px]">
      {!open ? (
        <button type="button" className="text-[#4B5563] underline underline-offset-2 hover:text-[#1F2937]" onClick={() => setOpen(true)}>Already set up in the box office? Use that setup</button>
      ) : (
        <div className="rounded-[10px] border border-[#E6E4E1] p-3 space-y-2">
          <div className="text-[#374151]">In the box office: <b>Printer setup → Summary → Copy support report</b>, then paste it here.</div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Paste the support report…" className="w-full rounded-md border border-[#E6E4E1] p-2 font-mono text-[11px]" />
          {err && <div className="text-[#9B1F1F]">{err}</div>}
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" variant="primary" disabled={!text.trim()} onClick={doImport}>Use this setup</Button>
          </div>
        </div>
      )}
    </div>
  );
};
